import { useTranslation } from 'react-i18next'
import { Navigate, Link, useLocation } from 'react-router-dom'
import { MailQuestion } from 'lucide-react'
import { tokenFromLocation } from '@/lib/linkToken'
import { Footer } from '@/components/Footer'

/**
 * There is no public sign-up form. Accounts exist because someone was invited
 * — the emailed link lands on /set-password, which is where the password (and
 * the account) is completed — or because the address is allowlisted, whose
 * door is the provider sign-in on /login. A visitor arriving here without a
 * link gets the honest answer instead of a form that can only refuse them:
 * Tricorder is in closed preview, and the way in is an invitation.
 *
 * A token in the URL means someone arrived with an emailed link on the wrong
 * path (an old link, a template change) — hand it to /set-password rather
 * than telling them the link is dead.
 */
export default function Register() {
  const { t } = useTranslation()
  const location = useLocation()
  const token = tokenFromLocation()

  if (token !== '') {
    // The token rides in the hash; carry it over or the link dies in the
    // hand-off (Navigate replaces the whole URL, hash included).
    return (
      <Navigate
        to={{ pathname: '/set-password', hash: location.hash, search: location.search }}
        replace
      />
    )
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <div className="flex-1 flex flex-col justify-center px-6 py-12">
        <div className="max-w-md mx-auto text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-primary/10 flex items-center justify-center">
            <MailQuestion className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-display font-semibold text-neutral-900 tracking-tight">
            {t('auth.registerClosedTitle')}
          </h1>
          <p className="mt-3 text-sm text-neutral-500">{t('auth.registerClosed')}</p>
          <p className="mt-8">
            <Link
              to="/login"
              className="inline-flex items-center justify-center h-11 px-6 text-sm font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/5 transition-colors"
            >
              {t('auth.setPassword.backToLogin')}
            </Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  )
}
